/**
 * Runtime localisation.
 *
 * The mechanism is Form Lock's and it is a good one: the choice lives in localStorage, a change
 * broadcasts a CustomEvent, and a hook subscribes so every mounted component re-renders together.
 * No provider, no context, no re-render of the tree from the root on every navigation.
 *
 * What changed is where the strings live. Form Lock keeps a 982-line TypeScript object of
 * `{ pt, en }` pairs; Vephus keeps JSON files per locale. Same runtime, but a translator can now
 * be handed a file, the two languages can be diffed against each other, and a missing key is a
 * missing key rather than a half-filled object literal.
 *
 * Defaults are inverted from Form Lock's, per the brief: en-US is primary, pt-BR secondary.
 */

import { useCallback, useEffect, useState } from 'react'
// Each language is a data file of its own, fetched when it is used (21/10/2026): the two of them were ~100 kB of the
// gzipped JavaScript everybody downloaded, though each person reads one. `?url` keeps them out of the JavaScript, with
// a hashed name, so a new version is never served from an old cache.
import enUrl from '../locales/en-US.json?url'
import ptUrl from '../locales/pt-BR.json?url'

export const LOCALES = ['en-US', 'pt-BR'] as const
export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en-US'

const STORAGE_KEY = 'devpulse:locale'
const CHANGE_EVENT = 'devpulse:locale-changed'

type Dictionary = Record<string, string>

const URLS: Record<Locale, string> = { 'en-US': enUrl, 'pt-BR': ptUrl }

/** The languages loaded so far. The app renders once the active one is here (main.tsx). */
const DICTIONARIES: Partial<Record<Locale, Dictionary>> = {}

/** A language's strings, given directly: the tests, which have both at hand. */
export function registerDictionary(locale: Locale, dictionary: Dictionary): void {
  DICTIONARIES[locale] = dictionary
}

const loading: Partial<Record<Locale, Promise<void>>> = {}

/** Fetches a language once; at once when it is already here. */
export function loadLocale(locale: Locale): Promise<void> {
  if (DICTIONARIES[locale]) return Promise.resolve()
  loading[locale] ??= fetch(URLS[locale])
    .then((response) => {
      if (!response.ok) throw new Error(`locale ${locale}: ${response.status}`)
      return response.json() as Promise<Dictionary>
    })
    .then((dictionary) => {
      DICTIONARIES[locale] = dictionary
    })
    .finally(() => {
      delete loading[locale]
    })
  return loading[locale]!
}

export const LOCALE_LABELS: Record<Locale, string> = {
  'en-US': 'English',
  'pt-BR': 'Português',
}

function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value)
}

export function getLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (isLocale(saved)) return saved
  } catch {
    // Private browsing, or storage blocked. Not a reason to fail to render.
  }

  // Only a pt-* browser gets Portuguese without asking. Everything else gets the default, which
  // is the inverse of Form Lock's rule and what the brief specifies.
  const browser = typeof navigator !== 'undefined' ? navigator.language?.toLowerCase() : ''
  return browser?.startsWith('pt') ? 'pt-BR' : DEFAULT_LOCALE
}

export function setLocale(locale: Locale): void {
  // The other language is fetched first, so the screen never shows keys in between.
  void loadLocale(locale)
    .catch(() => undefined)
    .then(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, locale)
      } catch {
        // Still dispatch: the change should apply for this session even if it cannot be remembered.
      }
      document.documentElement.lang = locale
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
    })
}

/**
 * One `Intl.PluralRules` per locale, built once.
 *
 * Constructing one is not free and this runs inside render.
 */
const PLURAL_RULES: Partial<Record<Locale, Intl.PluralRules>> = {}

function pluralCategory(locale: Locale, count: number): string {
  // CLDR counts 0 as one in Portuguese ("0 agente"); people write "0 agentes".
  if (count === 0 && locale === 'pt-BR') return 'other'
  let rules = PLURAL_RULES[locale]
  if (!rules) {
    rules = new Intl.PluralRules(locale)
    PLURAL_RULES[locale] = rules
  }
  return rules.select(count)
}

/**
 * Looks up a key, falling back through en-US to the key itself.
 *
 * Returning the key is deliberate. A missing translation then shows as `bugHunt.newThing` — ugly,
 * obvious, and greppable — rather than as an empty space that nobody notices until a customer does.
 *
 * **Plurals.** When `vars.count` is a number, `key.<category>` is tried before `key`, with the
 * category coming from `Intl.PluralRules` for the active locale. So `bugHunt.moreAgents.one` is
 * used for one and `bugHunt.moreAgents` for everything else, and a locale that needs more forms
 * than English does can add `.few` or `.many` without any code changing.
 *
 * This was found on screen rather than in review: the Bug Hunt showed a developer running two
 * agents as "+1 agentes ativos", which is wrong in Portuguese and wrong in English too. Any
 * counted noun in the interface has the same problem, so the fix belongs in the lookup and not at
 * one call site.
 */
export function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const count = vars?.count
  const plural = typeof count === 'number' ? `${key}.${pluralCategory(locale, count)}` : null

  const mine = DICTIONARIES[locale] ?? {}
  const fallback = DICTIONARIES[DEFAULT_LOCALE] ?? {}
  const template =
    (plural ? mine[plural] ?? fallback[plural] : undefined) ??
    mine[key] ??
    fallback[key] ??
    key

  if (!vars) return template

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

/** Every key present in the primary locale. Used by the completeness test. */
export function keysFor(locale: Locale): string[] {
  return Object.keys(DICTIONARIES[locale] ?? {})
}

export interface I18n {
  locale: Locale
  t: (key: string, vars?: Record<string, string | number>) => string
  setLocale: (locale: Locale) => void
}

export function useI18n(): I18n {
  const [locale, setLocaleState] = useState<Locale>(getLocale)

  useEffect(() => {
    // Another tab may have chosen a language this one has not fetched yet.
    const onChange = (): void => {
      const next = getLocale()
      void loadLocale(next).catch(() => undefined).then(() => setLocaleState(next))
    }
    window.addEventListener(CHANGE_EVENT, onChange)
    // Another tab changing the language should change this one too.
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  )

  return { locale, t, setLocale }
}
